/**
 * Sidebar Main Component
 *
 * @module shared/components/navigation/SidebarMain
 *
 * Main navigation sidebar with:
 * - Collapsible hamburger toggle
 * - Logo and branding
 * - Navigation menu with icons (upper half - scrollable)
 * - Queue panel with case cards (lower half - scrollable)
 * - User profile section
 * - Theme toggle switch
 *
 * Responsive Behavior:
 * - Desktop (≥ 960px): Sidebar always visible, pushes content
 * - Mobile (< 960px): Sidebar is off-screen when collapsed, overlays content when open
 *
 * Based on collection-ui-v7.html reference design
 */

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { memo, useState, useRef, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { SidebarProps } from './types';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './sidebarSettings';
import { scrollableStylesScrollOnly } from './styles';
import { NavigationMenu } from './NavigationMenu';
import { SidebarHeader } from './SidebarHeader';
import { ComponentErrorPage } from '../pages';
import { SidebarQueuePanel } from '@/modules/queue/presentation/shared';
import { UserProfileSection } from '@/modules/user/presentation/shared';

/**
 * Sidebar Main Component with responsive overlay behavior
 *
 * Mobile (< 960px): Drawer slides in/out as overlay
 * Desktop (≥ 960px): Drawer is always visible with push behavior
 */
const SCROLLBAR_VISIBLE_MS = 1000;

export const SidebarMain = memo(function SidebarMain({ collapsed = false, onToggle }: SidebarProps) {
  const muiTheme = useMuiTheme();
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleScroll = useCallback(() => {
    setIsScrolling(true);
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
      scrollTimeoutRef.current = null;
    }, SCROLLBAR_VISIBLE_MS);
  }, []);

  // Detect mobile view (breakpoint: < 960px)
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  // Mobile:
  // - Collapsed: keep a slim rail visible (so the user always has a toggle)
  // - Expanded: shown as overlay (handled by layout margin/backdrop)
  // Desktop: always visible, collapsed/expanded width
  const sidebarTransform = 'translateX(0)';

  return (
    <Box
      component="aside"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        // Animate both width (collapse/expand) and transform (mobile slide)
        transition: muiTheme.transitions.create(['width', 'transform'], {
          duration: muiTheme.transitions.duration.standard,
        }),
        transform: sidebarTransform,
        zIndex: muiTheme.zIndex.drawer,
        overflow: 'hidden',
        // Mobile-only shadow when open to enhance overlay effect
        boxShadow: isMobile && !collapsed ? 8 : 0,
      }}
    >
      {/* Header with Toggle and Logo */}
      <SidebarHeader collapsed={collapsed} onToggle={onToggle} />

      <Divider />

      {/* Single scrollable section: Menu + Queue (scrollbar small, visible only when scrolling) */}
      <Box
        onScroll={handleScroll}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          ...scrollableStylesScrollOnly,
          ...(isScrolling && {
            '&::-webkit-scrollbar-thumb': {
              background: muiTheme.palette.divider,
            },
            scrollbarColor: `${muiTheme.palette.divider} transparent`,
          }),
        }}
      >
        <NavigationMenu collapsed={collapsed} />
        <Divider />
        <ErrorBoundary
          fallbackRender={({ error, resetErrorBoundary }) => <ComponentErrorPage error={error} title="Queue unavailable" onRetry={resetErrorBoundary} />}
        >
          <SidebarQueuePanel collapsed={collapsed} />
        </ErrorBoundary>
      </Box>

      {/* User Profile with Theme Toggle - fixed at bottom */}
      <UserProfileSection collapsed={collapsed} />
    </Box>
  );
});

